import React, { useState } from "react";
import { AccessBehaviorProps } from "../AccessGate.types";
import { BenefitsModal } from "../../../components/common/BenefitsModal/BenefitsModal";

/**
 * Config accepted by PreviewBehavior through `behaviorConfig`.
 *
 * - `featureName` – human-readable name shown in the upsell modal.
 * - `onPreview`   – callback invoked when the user chooses to preview.
 */
export type PreviewBehaviorConfig = {
    featureName: string;
    onPreview: () => void;
};

/**
 * Behavior that intercepts clicks and shows an upsell / preview modal
 * when access is blocked. The child element is rendered normally but
 * its click is hijacked.
 *
 * @example
 * <AccessGate
 *   strategy={access}
 *   behavior={PreviewBehavior}
 *   behaviorConfig={{ featureName: "Advanced Metrics", onPreview: () => navigate("/preview") }}
 * >
 *   <Button>View Metrics</Button>
 * </AccessGate>
 */
export function PreviewBehavior({ children, config, decision }: AccessBehaviorProps) {
    const { featureName = "", onPreview = () => {} } = (config ?? {}) as PreviewBehaviorConfig;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const child = React.Children.only(children);

    const clonedChild = React.cloneElement(child, {
        onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsModalOpen(true);
        },
    });

    return (
        <>
            {clonedChild}
            <BenefitsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onPreview={() => {
                    setIsModalOpen(false);
                    onPreview();
                }}
                featureName={featureName}
                reason={decision.reason}
            />
        </>
    );
}
